/**
 * ScopeGate — page-level scope enforcement primitive.
 *
 * Wraps a route element and renders either the page content (if the current
 * operator has the required scope) or an AccessDeniedPanel (if not).
 *
 * Behaviour:
 *  - While scopes are not yet loaded: renders PageLoadingState to avoid a
 *    flash of denial.
 *  - Once loaded and operator has scope: renders children.
 *  - Once loaded and operator lacks scope: renders AccessDeniedPanel.
 *
 * This is a UX primitive, not a security boundary. The API gateway's
 * RequireScope middleware is the authoritative enforcement point.
 *
 * Usage in routes.tsx:
 * ```tsx
 * {
 *   path: 'signing-keys',
 *   element: (
 *     <ScopeGate required="identity:admin">
 *       <Suspense fallback={<PageLoadingState />}>
 *         <SigningKeysPage />
 *       </Suspense>
 *     </ScopeGate>
 *   ),
 * }
 * ```
 *
 * Task 3552c.
 */

import type { ReactNode } from 'react'
import { useOperatorStore } from '@/shared/stores/operator-store'
import { AccessDeniedPanel } from '@/shared/components/AccessDeniedPanel'
import { PageLoadingState } from '@/shared/components/PageLoadingState'

export interface ScopeGateProps {
  /** The scope(s) required to render children. */
  required: string | string[]
  /** Page content to render if access is permitted. */
  children: ReactNode
}

/**
 * Renders children if the operator has the required scope(s), otherwise
 * renders an AccessDeniedPanel. Shows a loading state while scopes are
 * still being fetched.
 */
export function ScopeGate({ required, children }: ScopeGateProps) {
  const scopesLoaded = useOperatorStore((s) => s.scopesLoaded)
  const hasScope = useOperatorStore((s) => s.hasScope)

  if (!scopesLoaded) {
    return <PageLoadingState label="Checking access..." />
  }

  if (!hasScope(required)) {
    return <AccessDeniedPanel required={required} />
  }

  return <>{children}</>
}
